import { useEffect, useRef, useState } from 'react';
import { wsClient } from '../services/websocket';
import type { BaseCandleBar, Interval } from '@ocktra/shared-types';


/**
 * Convert interval string to milliseconds
 */
function intervalToMilliseconds(interval: Interval): number {
    const intervalMap: Record<Interval, number> = {
        '1min': 60 * 1000,
        '5min': 5 * 60 * 1000,
        '15min': 15 * 60 * 1000,
        '30min': 30 * 60 * 1000,
        '60min': 60 * 60 * 1000,
        '1day': 24 * 60 * 60 * 1000,
        '1week': 7 * 24 * 60 * 60 * 1000,
        '1month': 30 * 24 * 60 * 60 * 1000,
    };

    return intervalMap[interval] || 60 * 1000; // Default to 1 minute
}

/**
 * Calculate candle time boundary based on timeframe
 * Returns the start time of the candle period in seconds
 */
function getCandleTimeBoundary(timestamp: number, intervalMs: number): number {
    // Floor the timestamp to the nearest interval boundary
    return Math.floor(timestamp / intervalMs) * intervalMs;
}

interface UseRealtimeCandlesProps {
    instrument: string;
    timeframe: Interval;
    initialCandles: BaseCandleBar[];
}

/**
 * Custom hook to handle real-time candle updates from WebSocket ticks
 * Creates new candles on timeframe boundaries and updates current candle
 */
export function useRealtimeCandles({ instrument, timeframe, initialCandles }: UseRealtimeCandlesProps) {
    const [candles, setCandles] = useState<BaseCandleBar[]>(initialCandles);
    const currentCandleTime = useRef<number | null>(null);
    const intervalMs = useRef<number>(intervalToMilliseconds(timeframe));

    // Update candles when initialCandles change
    useEffect(() => {
        setCandles(initialCandles);
        currentCandleTime.current = null;
        intervalMs.current = intervalToMilliseconds(timeframe);
    }, [initialCandles, timeframe]);

    // Subscribe to WebSocket ticks
    useEffect(() => {
        if (candles.length === 0) return;

        const unsubscribeTicks = wsClient.onChannelData('ticks', (ticks) => {
            ticks.forEach((tick: any) => {
                const tickSymbol = tick.symbol || tick.tradingSymbol;

                // Only process ticks for this instrument
                if (tickSymbol !== instrument) return;

                const price = tick.ltp || tick.last_price;
                if (!price) return;

                // Get tick timestamp (use current time if not provided)
                const tickTime = tick.timestamp ? new Date(tick.timestamp).getTime() : Date.now();

                // Calculate the candle time boundary for this tick based on timeframe
                const candleTimeBoundary = getCandleTimeBoundary(tickTime, intervalMs.current);
                const candleTimeSeconds = Math.floor(candleTimeBoundary / 1000); // Convert to seconds for chart

                setCandles(prevCandles => {
                    const updatedCandles = [...prevCandles];

                    // Check if we're in a new candle period
                    if (currentCandleTime.current === null || candleTimeBoundary !== currentCandleTime.current) {
                        // New candle period - create new candle
                        currentCandleTime.current = candleTimeBoundary;

                        const newCandle: BaseCandleBar = {
                            time: candleTimeSeconds as any,
                            open: price,
                            high: price,
                            low: price,
                            close: price,
                            volume: tick.volume || tick.last_traded_quantity || 0
                        };

                        console.log(`[RealtimeCandles] New ${timeframe} candle for ${instrument}:`, {
                            time: new Date(candleTimeBoundary).toISOString(),
                            price,
                            candle: newCandle
                        });

                        updatedCandles.push(newCandle);
                    } else {
                        // Same candle period - update last candle
                        const lastCandle = updatedCandles[updatedCandles.length - 1];
                        if (lastCandle) {
                            lastCandle.high = Math.max(lastCandle.high, price);
                            lastCandle.low = Math.min(lastCandle.low, price);
                            lastCandle.close = price;
                            lastCandle.volume = (lastCandle.volume || 0) + (tick.volume || tick.last_traded_quantity || 0);

                            console.log(`[RealtimeCandles] Updated ${timeframe} candle for ${instrument}:`, {
                                time: new Date(candleTimeBoundary).toISOString(),
                                price,
                                candle: lastCandle
                            });
                        }
                    }

                    return updatedCandles;
                });
            });
        });

        return () => {
            unsubscribeTicks();
        };
    }, [candles.length, instrument, timeframe]);

    return candles;
}
